import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { services, priorityOffers } from "../data/services";
import StarMotif from "../components/StarMotif";
import { Printer, ArrowLeft, Check } from "lucide-react";

/**
 * Brochure one-pager — printable, board-friendly summary of a priority offer.
 * Linked from BrochureModal success state and from the email confirmation.
 */
const Brochure = () => {
  const { slug } = useParams();
  const offer = priorityOffers.find((o) => o.slug === slug);
  const service = services.find((s) => s.slug === slug);

  useEffect(() => {
    document.title = offer
      ? `${offer.title} — Ekselens Consulting Brochure`
      : "Brochure not found — Ekselens Consulting";
  }, [offer]);

  if (!offer || !service) return <Navigate to="/services" replace />;

  return (
    <div className="bg-white">
      {/* Print controls — hidden when printing */}
      <div className="bg-zinc-100 border-b border-zinc-200 print:hidden">
        <div className="ek-container py-4 flex items-center justify-between gap-4">
          <Link to={`/services/${service.slug}`} className="inline-flex items-center gap-2 text-[#7737F6] text-sm font-medium hover:text-[#5D28C7]" data-testid="brochure-back-link">
            <ArrowLeft size={14} /> Back to service
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/brochure/${slug}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="ek-btn ek-btn-outline-dark text-sm py-2 px-4"
              data-testid="brochure-download-pdf"
            >
              <Printer size={14} /> Download PDF
            </a>
            <Link to="/contact" className="ek-btn ek-btn-primary text-sm py-2 px-4" data-testid="brochure-cta-discuss">
              Discuss this offer
            </Link>
          </div>
        </div>
      </div>

      {/* Brochure content */}
      <article className="ek-container max-w-4xl py-12 md:py-16 print:py-6" data-testid="brochure-content">
        {/* Header band */}
        <div className="ek-gradient-dark text-white relative overflow-hidden px-8 md:px-12 py-10 md:py-12 print:py-8">
          <div className="ek-star-watermark" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <StarMotif size={16} color="#A179FA" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#A179FA]">Ekselens Service Brochure</span>
            </div>
            <h1 className="ek-heading text-3xl md:text-4xl lg:text-[44px] leading-tight" data-testid="brochure-title">
              {offer.title}
            </h1>
            <p className="text-zinc-200 font-light text-lg mt-4 max-w-2xl">
              A focused, time-boxed engagement scoped to deliver clarity, evidence and momentum on {offer.title.toLowerCase()}.
            </p>
          </div>
        </div>

        {/* Three pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border-l border-r border-b border-zinc-200">
          <div className="bg-white p-7">
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#7737F6] mb-2">Who it helps</div>
            <p className="ek-body text-[15px]">{offer.who}</p>
          </div>
          <div className="bg-white p-7">
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#7737F6] mb-2">Typical challenge</div>
            <p className="ek-body text-[15px]">{offer.challenge}</p>
          </div>
          <div className="bg-white p-7">
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#7737F6] mb-2">What Ekselens delivers</div>
            <p className="ek-body text-[15px]">{offer.delivers}</p>
          </div>
        </div>

        {/* Intro */}
        <section className="mt-10">
          <h2 className="ek-heading text-2xl md:text-3xl text-black">Why this engagement</h2>
          <p className="ek-body text-lg mt-4">{service.intro}</p>
        </section>

        {/* Common challenges */}
        {service.challenges?.length > 0 && (
          <section className="mt-10">
            <h2 className="ek-heading text-2xl md:text-3xl text-black">The signals we hear</h2>
            <ul className="mt-5 space-y-3">
              {service.challenges.map((c, i) => (
                <li key={i} className="flex items-start gap-3 border-b border-zinc-200 pb-3">
                  <span className="text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] uppercase mt-1 shrink-0 w-7">0{i + 1}</span>
                  <p className="ek-body text-[15.5px] flex-1">{c}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* How we help */}
        <section className="mt-10">
          <h2 className="ek-heading text-2xl md:text-3xl text-black">How Ekselens helps</h2>
          <p className="ek-body text-lg mt-4">{service.howWeHelp}</p>
        </section>

        {/* What we deliver */}
        <section className="mt-10">
          <h2 className="ek-heading text-2xl md:text-3xl text-black">What we deliver</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
            {service.delivers.map((d, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-zinc-200">
                <div className="w-6 h-6 shrink-0 bg-[#F2F2F3] text-[#7737F6] flex items-center justify-center">
                  <Check size={12} strokeWidth={2.5} />
                </div>
                <p className="ek-body text-[14.5px] flex-1">{d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Typical outputs */}
        {service.outputs?.length > 0 && (
          <section className="mt-10">
            <h2 className="ek-heading text-2xl md:text-3xl text-black">Typical engagement outputs</h2>
            <ul className="mt-5 space-y-2">
              {service.outputs.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-[15.5px] ek-body">
                  <span className="text-[#7737F6] mt-1.5">●</span>
                  {o}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Why it matters */}
        <section className="mt-10 bg-[#F2F2F3] p-7 md:p-10 border-l-4 border-[#7737F6]">
          <h2 className="ek-heading text-2xl text-black">Why this matters</h2>
          <p className="ek-body text-[16.5px] mt-3">{service.whyItMatters}</p>
        </section>

        {/* Contact footer */}
        <section className="mt-12 border-t border-zinc-200 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#7737F6]">Next step</div>
              <h3 className="ek-heading text-xl text-black mt-2">Book a discovery conversation</h3>
              <p className="ek-body text-[14.5px] mt-2">
                A focused conversation will help us understand your context, identify the right starting point and define practical next steps.
              </p>
              <Link to="/contact" className="ek-btn ek-btn-primary mt-4" data-testid="brochure-footer-cta">
                Book now
              </Link>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#7737F6]">Contact</div>
              <div className="mt-3 text-[14px] text-zinc-700 space-y-1.5">
                <div>info@ekselens.com</div>
                <div>+44 (0) 20 7874 7217</div>
                <div>5th Floor, 167-169 Great Portland Street, London, W1W 5PF</div>
                <div className="text-[#7737F6] mt-3 font-medium">Technology-driven transformation. Practical delivery. Measurable value.</div>
              </div>
            </div>
          </div>
        </section>
      </article>

      <style>{`
        @media print {
          @page { margin: 14mm; }
          .ek-container { max-width: 100% !important; padding: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default Brochure;
